import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Space, 
  Typography, 
  Tag, 
  message,
  Input,
} from 'antd';
import { 
  BookOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const { Title, Text } = Typography;

const StudentClassesReminder = () => {
  const [studentsData, setStudentsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL;

  // Updated groupClassesByStudent function
  const groupClassesByStudent = (rawData) => {
    const studentsMap = {};
    
    rawData.forEach(row => {
      const studentId = row.student_id;
      
      if (!studentsMap[studentId]) {
        studentsMap[studentId] = {
          userid: studentId,
          firstname: row.firstname,
          lastname: row.lastname,
          nickname: row.student_nickname,
          phone: row.phone,
          email: row.email,
          totalClasses: row.total_classes,
          classGroups: {},
          hasEndingClasses: true
        };
      }
      
      const groupId = row.groupid;
      if (!studentsMap[studentId].classGroups[groupId]) {
        studentsMap[studentId].classGroups[groupId] = {
          groupid: groupId,
          classgroup_id: row.classgroup_id,
          class_code: row.class_code,
          subject: row.subject,
          teacher: row.teacher,
          upcomingClasses: [],
          nextClassDate: null,
          lastClassDate: null,
          remainingClassesInGroup: 0
        };
      }
      
      studentsMap[studentId].classGroups[groupId].upcomingClasses.push({
        classid: row.classid,
        date: row.date,
        schedule_day: row.schedule_day,
        schedule_time: row.schedule_time,
        schedule_hour: row.schedule_hour
      });
      
      studentsMap[studentId].classGroups[groupId].remainingClassesInGroup++;
    });
    
    // Convert classGroups object to array and calculate dates
    const studentsArray = Object.values(studentsMap).map(student => {
      const classGroupsArray = Object.values(student.classGroups);
      
      classGroupsArray.forEach(group => {
        if (group.upcomingClasses.length > 0) {
          const sortedClasses = group.upcomingClasses.sort((a, b) => 
            moment(a.date).valueOf() - moment(b.date).valueOf()
          );
          group.nextClassDate = sortedClasses[0].date;
          group.lastClassDate = sortedClasses[sortedClasses.length - 1].date;
          group.upcomingClasses = sortedClasses;
        }
      });
      
      student.classGroups = classGroupsArray;
      return student;
    });
    
    return studentsArray;
  };

  // Get overall urgency for a student
  const getStudentUrgency = (student) => {
    const totalClasses = student.totalClasses;
    
    if (totalClasses === 2) {
      return { color: 'red', text: 'Critical', icon: <WarningOutlined /> };
    } else if (totalClasses === 2) {
      return { color: 'orange', text: 'Urgent', icon: <ExclamationCircleOutlined /> };
    } else if (totalClasses <= 5) {
      return { color: 'yellow', text: 'Soon', icon: <ClockCircleOutlined /> };
    } else {
      return { color: 'green', text: 'Good', icon: <ClockCircleOutlined /> };
    }
  };

  // Fetch students with ending classes
  const fetchStudentsClasses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/student-reminder/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const studentsWithClasses = groupClassesByStudent(response.data);
      
      setStudentsData(studentsWithClasses);
      setFilteredData(studentsWithClasses);

    } catch (error) {
      console.error('Error fetching all students with classes:', error);
      message.error('Failed to fetch students data');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
    
    if (!value) {
      setFilteredData(studentsData);
      return;
    }
    
    const filtered = studentsData.filter(student => 
      student.firstname?.toLowerCase().includes(value.toLowerCase()) ||
      student.lastname?.toLowerCase().includes(value.toLowerCase()) ||
      student.nickname?.toLowerCase().includes(value.toLowerCase()) ||
      student.email?.toLowerCase().includes(value.toLowerCase()) ||
      student.classGroups.some(group => 
        group.class_code?.toLowerCase().includes(value.toLowerCase()) ||
        group.subject?.toLowerCase().includes(value.toLowerCase())
      )
    );
    
    setFilteredData(filtered);
  };

  useEffect(() => {
    fetchStudentsClasses();
  }, []);
  
  // Table columns
  const columns = [
    {
      title: 'Student',
      key: 'student',
      width: 200,
      render: (_, record) => (
        <div>
          <Text strong>{record.firstname} {record.lastname}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.nickname} | {record.email}
          </Text>
        </div>
      ),
    },
    {
      title: 'Enrolled Courses',
      key: 'classes',
      render: (_, record) => (
        <div>
          {record.classGroups.map(group => (
            <div key={group.groupid} style={{ marginBottom: '8px' }}>
              <Space>
                <Tag color="blue">{group.class_code}</Tag>
                <Text>{group.subject}</Text>
                <Tag color="purple">{group.remainingClassesInGroup} left</Tag>
              </Space>
              <br />
              <Text type="secondary" style={{ fontSize: '11px' }}>
                Teacher: {group.teacher} | Next: {moment(group.nextClassDate).format('DD MMM')}
              </Text>
            </div>
          ))}
          <div style={{ marginTop: '8px', padding: '4px 8px', backgroundColor: '#fff2e8', borderRadius: '4px', border: '1px solid #ffb366' }}>
            <Text strong style={{ color: '#d4380d' }}>
              Total: {record.totalClasses} classes remaining
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Urgency Level',
      key: 'urgency',
      width: 120,
      render: (_, record) => {
        const urgency = getStudentUrgency(record);
        
        return (
          <div>
            <Tag color={urgency.color} icon={urgency.icon}>
              {urgency.text}
            </Tag>
            <br />
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {record.totalClasses} classes left
            </Text>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3}>
            <BookOutlined /> Course End Reminder
          </Title>
          <Space>
            <Input
              placeholder="Search students or classes..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchStudentsClasses}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="userid"
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} students`
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                <Title level={5}>Upcoming Classes Schedule</Title>
                {record.classGroups.map(group => (
                  <Card key={group.groupid} size="small" style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <Text strong>{group.class_code} - {group.subject}</Text>
                        <br />
                        <Text type="secondary">Teacher: {group.teacher}</Text>
                        <br />
                        <Text style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                          ⚠️ Only {group.remainingClassesInGroup} classes remaining in this course
                        </Text>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <Tag color="blue">
                          Next: {moment(group.nextClassDate).format('DD MMM YYYY')}
                        </Tag>
                        <br />
                        <Tag color="red">
                          Final: {moment(group.lastClassDate).format('DD MMM YYYY')}
                        </Tag>
                      </div>
                    </div>
                    
                    <div style={{ marginTop: '12px' }}>
                      <Text strong>Upcoming Schedule:</Text>
                      <div style={{ marginTop: '8px' }}>
                        {group.upcomingClasses.map((classItem, index) => (
                          <div key={classItem.classid} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            padding: '4px 8px',
                            backgroundColor: index === 0 ? '#e6f7ff' : (index % 2 === 0 ? '#fff' : '#f9f9f9'),
                            borderRadius: '4px',
                            marginBottom: '2px',
                            border: index === 0 ? '1px solid #1890ff' : 'none'
                          }}>
                            <span>
                              {index === 0 ? '📅 Next Class' : `Class ${index + 1}`}
                              {index === group.upcomingClasses.length - 1 ? ' (Final)' : ''}
                            </span>
                            <span>
                              <Text strong>{moment(classItem.date).format('DD MMM YYYY')}</Text>
                              <Text type="secondary"> at {classItem.schedule_time}</Text>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
                
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '8px', border: '1px solid #ffd591' }}>
                  <Text strong style={{ color: '#d46b08' }}>
                    📊 Summary: {record.firstname} has {record.totalClasses} total classes remaining across all enrolled courses
                  </Text>
                </div>
              </div>
            ),
            rowExpandable: (record) => record.classGroups.length > 0,
          }}
        />
      </Card>
    </div>
  );
};

export default StudentClassesReminder;